import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';

import { cloneDeep } from 'lodash';
import { RvSortableItem, RvSortableService } from 'src/app/rv-sortable';

import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { BaseImageService } from '../services/base-image.service';
import { FileExistenceCheckService } from '../services/file-existence-check.service';
import { FileMetadataUtilsService } from '../services/file-metadata-utils.service';
import { LogoImageService } from '../services/logo-image.service';
import { StockLibraryService } from '../services/stock-library.service';
import {
  ImageAiService,
  ImageAiStorageFile,
} from '../services/image-ai.service';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';

interface ImageItem extends RvSortableItem {
  id: string;
  name: string;
  file?: string;
  thumbnail?: string;
  previewFailed?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'template-component-image',
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  standalone: false,
})
export class ImageComponent implements OnInit {
  static readonly SIZING_FILL = 'cover';
  static readonly SIZING_FIT = 'contain';

  readonly SIZING_FILL = ImageComponent.SIZING_FILL;
  readonly SIZING_FIT = ImageComponent.SIZING_FIT;

  private imageService: any = this.baseImageService;

  @ViewChildren('sizingOptionBtn')
  sizingOptionBtns!: QueryList<ElementRef<HTMLButtonElement>>;

  public componentId: string;

  uploadManager: any;
  isUploading: boolean = false;
  fileUploadList: File[] = [];
  boundAddFile = this.addFile.bind(this);
  values = {
    duration: 0,
    transition: null,
    sizing: ImageComponent.SIZING_FIT,
  };
  isDefaultImageList: boolean;
  selectedImages: ImageItem[] = [];
  helpText: string;
  sortableService: RvSortableService<ImageItem>;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    public templateEditorService: TemplateEditorService,
    private templateEditorUtils: TemplateEditorUtilsService,
    private storageUtilsService: StorageUtilsService,
    private baseImageService: BaseImageService,
    private logoImageService: LogoImageService,
    private storageManagerService: StorageManagerService,
    private fileMetadataUtilsService: FileMetadataUtilsService,
    private fileExistenceCheckService: FileExistenceCheckService,
    public stockLibraryService: StockLibraryService,
    public currentPlanService: CurrentPlanService,
    public featuresService: FeaturesService,
    private imageAiService: ImageAiService,
    private aiCreditsService: AiCreditsService,
    private analyticsService: AnalyticsService,
    private companyStateService: CompanyStateService,
  ) {
    this.componentsService.registerDirective({
      type: 'rise-image',
      element: this.elementRef.nativeElement,
      show: () => {
        this.imageService = this.baseImageService;
        this.imageService.componentId = this.componentsService.selected.id;

        this._init();
      },
      onPresentationOpen: () => {
        this.baseImageService.componentId = null;
      },
      getName: (componentId) => {
        const files = this._getFilesFor(componentId);

        if (files && files.length > 0) {
          return this.storageUtilsService.fileNameOf(files[0]);
        } else {
          return null;
        }
      },
      onAiHandler: () => {
        if (!this.canGenerateWithAI()) {
          return false;
        }

        this.generateWithAI();
        return true;
      },
    });

    this.componentsService.registerDirective({
      type: 'rise-image-logo',
      element: this.elementRef.nativeElement,
      show: () => {
        this.imageService = this.logoImageService;
        this.imageService.componentId = null;

        this._init();
      },
    });

    this.uploadManager = {
      uploadedFiles: [],
      onUploadStatus: (isUploading) => {
        const wasUploading = this.isUploading;
        this.isUploading = isUploading;

        if (!wasUploading && isUploading) {
          this.uploadManager.uploadedFiles = [];
        }

        if (wasUploading && !isUploading) {
          const uploadedFiles = this.uploadManager.uploadedFiles;
          this.uploadManager.uploadedFiles = [];

          if (uploadedFiles.length) {
            this._addFilesToMetadata(
              this.storageUtilsService.sortFilesByName(uploadedFiles),
            );
          }
        }
      },
      addFile: (file) => {
        this.uploadManager.uploadedFiles.push(file);
      },
      isSingleFileSelector: this.isEditingLogo.bind(this),
    };

    this._reset();
  }

  ngOnInit() {
    this.sortableService = new RvSortableService<ImageItem>({
      items: this.selectedImages,
      getDisplayName: (item) => item.name || 'Image',
      onReorder: (items) => this.handleReorder(items),
      hostElement: this.elementRef,
      appendTo: '.component-container',
    });
  }

  private handleReorder(items: ImageItem[]): void {
    this.imageService.updateMetadata(
      items.map((item) => ({
        file: item.file,
        exists: item.exists,
        'thumbnail-url': item['thumbnail-url'],
        'time-created': item['time-created'],
      })),
    );
  }

  isEditingLogo() {
    return this.imageService === this.logoImageService;
  }

  get uploadButtonLabel(): string {
    if (this.isEditingLogo()) {
      return 'Upload a Logo';
    }

    return this.selectedImages.length === 1 ? 'Replace Image' : 'Upload Images';
  }

  isResponsive() {
    return !this.isEditingLogo() && this.imageService.isResponsive();
  }

  _reset() {
    this._setSelectedImages([]);
  }

  _addFilesToMetadata(files, alwaysAppend?, replaceExisting?) {
    const selectedFiles =
      this.isDefaultImageList || replaceExisting ? [] : this.selectedImages;
    let metadata = this.fileMetadataUtilsService.metadataWithFile(
      selectedFiles,
      LogoImageService.DEFAULT_IMAGE_THUMBNAIL,
      files,
      alwaysAppend,
    );

    metadata = this.imageService.updateMetadata(metadata);

    this._setSelectedImages(metadata);
  }

  _loadSelectedImages() {
    const selectedImages = this.imageService.getImagesAsMetadata();

    if (selectedImages) {
      this._setSelectedImages(selectedImages);
    }

    this.templateEditorService.loadingPresentation = true;

    this._checkFileExistenceFor(this.imageService.componentId).finally(() => {
      this.templateEditorService.loadingPresentation = false;
    });
  }

  _getFilesFor(componentId) {
    const metadata = this.attributeDataService.getAttributeData(
      componentId,
      'metadata',
    );

    if (!metadata) {
      const files = this.attributeDataService.getAvailableAttributeData(
        componentId,
        'files',
      );

      return this.fileMetadataUtilsService.filesAttributeToArray(files);
    }

    return this.fileMetadataUtilsService.extractFileNamesFrom(metadata);
  }

  updateFileMetadata(componentId, newMetadata) {
    const currentMetadata = this.attributeDataService.getAttributeData(
      componentId,
      'metadata',
    );
    const updatedMetadata =
      this.fileMetadataUtilsService.getUpdatedFileMetadata(
        currentMetadata,
        newMetadata,
      );

    if (updatedMetadata) {
      this._setMetadata(componentId, updatedMetadata);
    }
  }

  _setMetadata(componentId, metadata) {
    const selectedImages = cloneDeep(metadata);
    const filesAttribute =
      this.fileMetadataUtilsService.filesAttributeFor(selectedImages);

    if (componentId === this.imageService.componentId) {
      this._setSelectedImages(selectedImages);
    }

    const metadataToSave =
      this.fileMetadataUtilsService.metadataForSave(metadata);
    this.attributeDataService.setAttributeData(
      componentId,
      'metadata',
      metadataToSave,
    );
    this.attributeDataService.setAttributeData(
      componentId,
      'files',
      filesAttribute,
    );
  }

  _setSelectedImages(selectedImages) {
    const filesAttribute =
      this.fileMetadataUtilsService.filesAttributeFor(selectedImages);

    // Show logo in the image list if component is set to use logo and a logo is available
    const logoAsMetadata = this.logoImageService.getImagesAsMetadata();
    if (
      !this.isEditingLogo() &&
      this.imageService.isSetAsLogo() &&
      logoAsMetadata.length > 0
    ) {
      this.selectedImages = this.ensureImageItemsHaveIds(logoAsMetadata);
    } else {
      this.selectedImages = this.ensureImageItemsHaveIds(selectedImages);
    }

    // Update sortable service
    if (this.sortableService) {
      this.sortableService.updateItems(this.selectedImages);
    }

    this.isDefaultImageList =
      filesAttribute === this.imageService.getBlueprintData('files');
  }

  private ensureImageItemsHaveIds(images: any[]): ImageItem[] {
    return images.map((image, index) => {
      const imageCopy = cloneDeep(image);
      if (!imageCopy.id) {
        imageCopy.id = `image-${Date.now()}-${index}`;
      }
      return imageCopy as ImageItem;
    });
  }

  saveDuration() {
    this.imageService.setDuration(this.values.duration);
  }

  saveTransition() {
    this.imageService.setTransition(this.values.transition);
  }

  setSizing(sizing: string) {
    if (this.isEditingLogo() || sizing === this.values.sizing) {
      return;
    }

    this.values.sizing = sizing;
    this.imageService.setSizing(sizing);

    this.analyticsService.track('Image Display Changed', {
      companyId: this.companyStateService.getSelectedCompanyId(),
      displayType: sizing === ImageComponent.SIZING_FILL ? 'Fill' : 'Fit',
    });
  }

  /**
   * Handles keyboard navigation within the image display radiogroup.
   * Implements the ARIA radiogroup pattern: arrow keys move focus and
   * selection, only the selected option is in the tab sequence.
   */
  onSizingKeydown(event: KeyboardEvent, sizing: string) {
    const order = [ImageComponent.SIZING_FILL, ImageComponent.SIZING_FIT];
    const idx = order.indexOf(sizing);
    let nextIdx: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (idx + 1) % order.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx = (idx - 1 + order.length) % order.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = order.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setSizing(order[nextIdx]);
    this.sizingOptionBtns?.get(nextIdx)?.nativeElement.focus();
  }

  _init() {
    this._reset();

    this.storageManagerService.fileType = 'image';
    this.storageManagerService.isSingleFileSelector =
      this.isEditingLogo.bind(this);
    this.storageManagerService.onSelectHandler = (newSelectedItems) => {
      this._addFilesToMetadata(newSelectedItems, true);
    };

    this._loadSelectedImages();

    const duration = this.imageService.getAvailableAttributeData('duration');

    // default to value 10 if duration not defined
    this.values.duration = this.templateEditorUtils.intValueFor(duration, 10);

    this.values.transition =
      this.imageService.getAvailableAttributeData('transition');
    this.values.sizing = this.isEditingLogo()
      ? ImageComponent.SIZING_FIT
      : this.imageService.getSizing();
    this.helpText = this.imageService.getHelpText();
  }

  _checkFileExistenceFor(componentId): Promise<void> {
    if (!componentId) {
      return Promise.resolve();
    }

    const files = this._getFilesFor(componentId);

    return this.fileExistenceCheckService
      .requestMetadataFor(files, LogoImageService.DEFAULT_IMAGE_THUMBNAIL)
      .then((metadata) => {
        this.updateFileMetadata(componentId, metadata);
      })
      .catch((error) => {
        console.error(
          'Could not check file existence for: ' + componentId,
          error,
        );
      });
  }

  selectFromStorage() {
    this.componentsService.editComponent({
      type: 'rise-storage-selector',
    });
  }

  activateImageUploader() {
    const uploader = document.getElementById(
      'image-list-uploader',
    ) as HTMLInputElement;
    if (uploader) {
      uploader.click();
    }
  }

  removeImageFromList(image) {
    this.imageService
      .removeImage(image, this.selectedImages)
      .then((updatedMetadata) => {
        setTimeout(() => {
          //defer to the next macrotask so change detection picks up the update
          this._setSelectedImages(updatedMetadata);
        });
      });
  }

  sortItem(evt) {
    const oldIndex = evt.data.oldIndex;
    const newIndex = evt.data.newIndex;

    this.selectedImages.splice(
      newIndex,
      0,
      this.selectedImages.splice(oldIndex, 1)[0],
    );
  }

  addFile(file: ImageAiStorageFile | File) {
    this._addFilesToMetadata([file]);
  }

  canGenerateWithAI(): boolean {
    return this.aiCreditsService.canUseAi();
  }

  generateWithAI() {
    this.imageAiService.showGenerateImage((file) =>
      this.applyGeneratedImage(file),
    );
  }

  private applyGeneratedImage(file: ImageAiStorageFile | File) {
    const replaceExisting =
      this.isEditingLogo() || this.selectedImages.length <= 1;
    this._addFilesToMetadata([file], false, replaceExisting);
  }
}
